import { useCallback, useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Chip, Typography, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { getAllClaims, regenerateAllClaims, recordAsternReceiptBulk } from '../../api/incentiveClaimApi';
import { getAllCampaigns } from '../../api/incentiveApi';
import { useUser } from '../../context/userContext';
import useTableState from '../../hooks/useTableState';
import IncentiveAsternModal from './incentiveAsternModal';
import IncentivePayoutModal from './incentivePayoutModal';
import { facetFilterOptions } from '../common/facetFilter';

const STATUS_OPTIONS = ['In Progress', 'Ready to Claim', 'Eligible', 'On Slip', 'Received', 'Completed', 'Void', 'Archived'];

const STATUS_CHIP = {
    'In Progress': { bg: '#FEF3C7', color: '#92400E' },
    // Customer has paid in full, so this is the next thing to claim from Astern. It is NOT
    // payable yet — recording the receipt turns it Eligible. (user 2026-09-20)
    'Ready to Claim': { bg: '#DCFCE7', color: '#166534' },
    Eligible: { bg: '#E0E7FF', color: '#3730A3' },
    'On Slip': { bg: '#CCFBF1', color: '#115E59' },
    Received: { bg: '#DBEAFE', color: '#1E3A8A' },
    Completed: { bg: '#D1FAE5', color: '#065F46' },
    Void: { bg: '#FEE2E2', color: '#991B1B' },
    Archived: { bg: '#E2E8F0', color: '#475569' },
};

const StatusChip = ({ value }) => {
    const cfg = STATUS_CHIP[value] || { bg: '#F1F5F9', color: '#64748B' };
    return (
        <Chip
            label={value || '—'}
            size="small"
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }}
        />
    );
};

export default function IncentiveClaimsList() {
    const ts = useTableState('', { defaultSorting: [{ id: 'salesId', desc: true }] });
    const [data, setData] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignId, setCampaignId] = useState('');
    const [agentFilter, setAgentFilter] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [asternRow, setAsternRow] = useState(null);
    const [payoutRow, setPayoutRow] = useState(null);
    const [caseChildren, setCaseChildren] = useState(null);
    const { user } = useUser();
    const isSuperAdmin = !!user?.roles?.includes('Super Admin');
    const [regenerating, setRegenerating] = useState(false);
    // Bulk "Mark Received from Astern": select recordable Total-Incentive rows, apply ONE date.
    const [rowSelection, setRowSelection] = useState({});
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkDate, setBulkDate] = useState('');
    const [bulkRemark, setBulkRemark] = useState('');
    const [bulkBusy, setBulkBusy] = useState(false);

    useEffect(() => {
        getAllCampaigns()
            .then(r => {
                const list = Array.isArray(r.data) ? r.data : [];
                setCampaigns(list);
                // Deliberately NOT auto-selecting list[0]. campaignId starts '' = "All
                // Campaigns", which is what this page is meant to open on; jumping to the
                // newest campaign hid every other campaign's claims behind a picker nobody
                // thought to change. Tan Lee Khoon's July RM300 looked missing for exactly
                // this reason — the page had opened on August. (user 2026-08-28)
            })
            .catch(() => setCampaigns([]));
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getAllClaims({ campaignId });
            setData(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            console.error('Failed to load incentive claims:', e);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [campaignId]);

    // Agents to offer in the filter, built from the claims actually loaded. Only rows that
    // belong to a person — the Webbey "Total Incentive" / "Webbey Incentive" rows are the
    // company side and carry no beneficiary worth filtering on.
    const agentOptions = useMemo(() => {
        const seen = new Map();
        data.forEach(d => {
            if (d.rowType !== 'Incentive') return;
            if (!d.beneficiaryAgentId) return;
            if (!seen.has(d.beneficiaryAgentId)) seen.set(d.beneficiaryAgentId, d.agentName || `#${d.beneficiaryAgentId}`);
        });
        return [...seen.entries()]
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [data]);

    // Real name behind a company-side row. The API names every non-Incentive row "Webbey"
    // but still stamps beneficiaryAgentId, so the agent's own paired Incentive row supplies
    // the name. Returns '' for the agent's own rows (already named) and for rows with no
    // beneficiary — the Webbey Incentive residual on an Addon campaign, for instance.
    const nameByAgentId = useMemo(() => {
        const m = new Map();
        data.forEach(d => {
            if (d.rowType === 'Incentive' && d.beneficiaryAgentId && d.agentName) {
                m.set(d.beneficiaryAgentId, d.agentName);
            }
        });
        return m;
    }, [data]);
    const beneficiaryName = useCallback((row) => {
        if (!row || row.rowType === 'Incentive') return '';
        if (!row.beneficiaryAgentId) return '';
        return nameByAgentId.get(row.beneficiaryAgentId) ?? '';
    }, [nameByAgentId]);

    const visibleData = useMemo(() => {
        let rows = data;
        if (status) rows = rows.filter(d => (d.displayStatus ?? 'In Progress') === status);
        // Filtering by agent keeps that agent's own Incentive rows AND the Webbey rows for
        // the same campaign+sale, so the Astern-receipt workflow stays reachable — hiding
        // the Total Incentive row would strand the money the payout depends on.
        if (agentFilter) {
            const id = Number(agentFilter);
            const keys = new Set(
                data.filter(d => d.rowType === 'Incentive' && d.beneficiaryAgentId === id)
                    .map(d => `${d.campaignId}|${d.salesId}`)
            );
            rows = rows.filter(d =>
                (d.rowType === 'Incentive' && d.beneficiaryAgentId === id)
                || (d.rowType !== 'Incentive' && keys.has(`${d.campaignId}|${d.salesId}`))
            );
        }
        return rows;
    }, [data, status, agentFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleRegenerateAll = async () => {
        if (!window.confirm(
            'Regenerate ALL incentive claims from current sales + commission data?\n\n'
            + 'This only ADDS missing claims and refreshes amounts. It never deletes claims, '
            + 'never touches commissions, and never changes rows you have already marked Received/Paid/On-Slip. '
            + 'Safe to run.'
        )) return;
        setRegenerating(true);
        try {
            // The sweep now runs in the BACKGROUND (visits ~1,900 sales — too long for one
            // request), so the response returns immediately. Wait a bit, then refresh.
            const r = await regenerateAllClaims();
            const msg = r.data?.message || 'Regenerate started.';
            if (r.data?.started === false) {
                window.alert(msg);
            } else {
                window.alert(msg + '\n\nGive it about a minute to finish, then this list will refresh.');
                // Auto-refresh after ~75s so the completed results show without a manual reload.
                setTimeout(() => { fetchData(); }, 75000);
            }
        } catch (e) {
            console.error('Regenerate all failed:', e);
            window.alert('Regenerate failed: ' + (e?.response?.data || e?.message || 'unknown error'));
        } finally {
            setRegenerating(false);
        }
    };


    const columns = useMemo(() => [
        { id: 'salesId', header: 'Sales #', accessorFn: row => row.salesId, size: 80,
          Cell: ({ row }) => (row.original.salesId > 0 ? row.original.salesId : '—') },
        { id: 'customerName', header: 'Customer', accessorFn: row => row.customerName ?? '', size: 150 },
        // Every non-Incentive row is named "Webbey" by the API, because that money is owed
        // by Astern to the company rather than to a person. But on a Cases campaign the
        // Total Incentive row is created PER AGENT — so eight rows all reading "Webbey" hid
        // the only thing you needed: whose RM300 it is. The row carries beneficiaryAgentId,
        // so name it from the paired Incentive row and search on that too. (user 2026-08-28)
        {
            id: 'agentName', header: 'Agent', size: 190,
            accessorFn: row => {
                const own = row.agentName ?? '';
                const forWhom = beneficiaryName(row);
                return forWhom ? `${own} ${forWhom}` : own;
            },
            Cell: ({ row }) => {
                const r = row.original;
                const forWhom = beneficiaryName(r);
                if (!forWhom) return r.agentName ?? '';
                return (
                    <span>
                        {r.agentName}
                        <span style={{ color: '#64748B', fontSize: '0.75rem', display: 'block' }}>
                            for {forWhom}
                        </span>
                    </span>
                );
            },
        },
        { id: 'rowType', header: 'Type', accessorKey: 'rowType', size: 130 },
        { id: 'campaignName', header: 'Campaign', accessorFn: row => row.campaignName ?? '', size: 160 },
        { id: 'metric', header: 'Metric', accessorKey: 'metric', size: 100 },
        {
            id: 'contribution', header: 'Contribution', size: 110,
            accessorFn: row => row.contribution ?? 0,
            Cell: ({ row }) => {
                const v = row.original.contribution;
                return v != null ? new Intl.NumberFormat('en-MY', { maximumFractionDigits: 2 }).format(v) : '—';
            },
        },
        {
            id: 'amount', header: 'Amount', size: 110,
            accessorFn: row => row.rowType === 'Total Incentive' ? (row.asternAmount ?? 0) : (row.rewardType === 'Cash' ? (row.amount ?? 0) : -1),
            Cell: ({ row }) => {
                const r = row.original;
                if (r.rowType === 'Total Incentive') return handlePriceWithRM(r.asternAmount ?? 0);
                return r.rewardType === 'Cash' ? handlePriceWithRM(r.amount ?? 0) : '—';
            },
        },
        {
            id: 'reward', header: 'Reward', size: 140,
            accessorFn: row => row.rewardType !== 'Cash' ? (row.rewardLabel ?? '') : '',
            Cell: ({ row }) => {
                const r = row.original;
                return r.rewardType !== 'Cash' ? (r.rewardLabel || '—') : '';
            },
        },
        {
            id: 'asternReceived', header: 'Received from Astern?', size: 150,
            accessorFn: row => row.asternReceived ? 'Yes' : 'No',
            Cell: ({ row }) => (
                <span style={{ color: row.original.asternReceived ? '#065F46' : '#94A3B8', fontWeight: 600, fontSize: '0.75rem' }}>
                    {row.original.asternReceived ? 'Yes' : 'No'}
                </span>
            ),
        },
        {
            id: 'commProgress', header: 'Comm % (Astern)', size: 120,
            accessorFn: row => row.commProgressPercent ?? -1,
            Cell: ({ row }) => {
                const r = row.original;
                // Shown on EVERY row type. It is a per-sale figure, and gating it to the
                // Total Incentive row (the one named 'Webbey') meant filtering by agent hid
                // it entirely. (user 2026-08-26)
                if (r.commProgressPercent == null) return '—';
                const pct = r.commProgressPercent;
                return <span style={{ color: pct >= 100 ? '#065F46' : '#92400E', fontWeight: 600, fontSize: '0.75rem' }}>{pct}%</span>;
            },
        },
        {
            // What the CUSTOMER has paid, beside what Astern has paid us. A battery
            // incentive is only really claimable once the customer money is in, so the
            // two percentages belong next to each other. Per-sale figure, shown on every
            // row type for the same reason Comm % is. (user 2026-09-08)
            id: 'custProgress', header: 'Cust Progress', size: 120,
            accessorFn: row => row.custProgressPercent ?? -1,
            Cell: ({ row }) => {
                const r = row.original;
                if (r.custProgressPercent == null) return '—';
                const pct = Number(r.custProgressPercent);
                const money = (v) => v == null ? '—' :
                    'RM ' + Number(v).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return (
                    <span
                        title={`Customer paid ${money(r.customerPaid)} of ${money(r.customerTotal)}`}
                        style={{
                            color: pct >= 100 ? '#065F46' : pct >= 60 ? '#0E8FD1' : '#92400E',
                            fontWeight: 600, fontSize: '0.75rem', cursor: 'help',
                        }}
                    >
                        {pct}%
                    </span>
                );
            },
        },
        {
            id: 'status', header: 'Status', size: 110,
            accessorFn: row => row.displayStatus ?? 'In Progress',
            Cell: ({ row }) => <StatusChip value={row.original.displayStatus} />,
        },
        {
            // Why a claim is Void (or how a live claim was computed). Voided rows show the
            // reason the sweep removed them, e.g. "Battery removed — 0 units" or "Campaign
            // period changed". (user 2026-08-07)
            id: 'reason', header: 'Reason / Note', size: 240,
            accessorFn: row => row.formulaNote ?? '',
            Cell: ({ row }) => {
                const note = row.original?.formulaNote ?? '';
                const isVoid = (row.original?.displayStatus === 'Void');
                if (!note) return '';
                return <span style={{ fontSize: '0.72rem', color: isVoid ? '#991B1B' : '#64748B', fontWeight: isVoid ? 600 : 400 }}>{note}</span>;
            },
        },
        // beneficiaryName changes once the claims land, so the columns must rebuild with it —
        // an empty dep array would freeze the first (empty) lookup and never show the names.
    ], [beneficiaryName]);

    // CSV export — mirrors the All Sales Comm export (export-to-csv). Exports the currently
    // visible (status-filtered) rows, columns matching the table. (user 2026-07-22)
    const csvConfig = mkConfig({ fieldSeparator: ',', useKeysAsHeaders: true, filename: 'Incentive_Claims_Export' });
    const mapIncentiveForExport = (rows) => (rows || []).map(r => ({
        'Sales #': r.salesId > 0 ? r.salesId : '',
        'Customer': r.customerName ?? '',
        'Agent': r.agentName ?? '',
        'Type': r.rowType ?? '',
        'Campaign': r.campaignName ?? '',
        'Metric': r.metric ?? '',
        'Contribution': r.contribution ?? '',
        'Amount (RM)': r.rowType === 'Total Incentive' ? (r.asternAmount ?? 0) : (r.rewardType === 'Cash' ? (r.amount ?? 0) : ''),
        'Reward': r.rewardType !== 'Cash' ? (r.rewardLabel ?? '') : '',
        'Received from Astern': r.asternReceived ? 'Yes' : 'No',
        'Comm % (Astern)': r.commProgressPercent != null ? r.commProgressPercent : '',
        // What the CUSTOMER has paid, beside what Astern has paid us — a battery
        // incentive is only really claimable once the customer money is in.
        'Cust Progress %': r.custProgressPercent != null ? r.custProgressPercent : '',
        'Customer Paid': r.customerPaid ?? '',
        'Customer Total': r.customerTotal ?? '',
        'Status': r.displayStatus ?? 'In Progress',
        'Reason / Note': r.formulaNote ?? '',
    }));
    const handleExportCsv = () => {
        const rows = mapIncentiveForExport(visibleData);
        const csv = generateCsv(csvConfig)(rows.length ? rows : [{}]);
        download(csvConfig)(csv);
    };

    // "Ready to Claim" export — the incentive twin of the commission page's export of the
    // same name: ONE line per sale whose customer has paid in full but whose battery money
    // hasn't been marked received from Astern yet, i.e. exactly what to claim from Astern.
    // Uses every loaded row (campaign / agent filters apply, the status filter does not),
    // so the list is complete whatever status is showing. (user 2026-09-21)
    const handleExportReadyToClaim = () => {
        const bySale = new Map();
        (data || []).filter(r => r.rowType === 'Incentive' && r.displayStatus === 'Ready to Claim').forEach(r => {
            const key = `${r.campaignId}-${r.salesId}`;
            const cur = bySale.get(key) || { r, agents: [], units: 0, amount: 0 };
            cur.agents.push(`${r.agentName ?? '—'} (RM ${(Number(r.amount) || 0).toFixed(2)})`);
            cur.units += Number(r.contribution) || 0;
            cur.amount += Number(r.amount) || 0;
            bySale.set(key, cur);
        });
        const rows = [...bySale.values()]
            .sort((a, b) => (a.r.campaignName || '').localeCompare(b.r.campaignName || '') || a.r.salesId - b.r.salesId)
            .map(({ r, agents, units, amount }) => ({
                'Campaign': r.campaignName ?? '',
                'Sales #': r.salesId,
                'Customer': r.customerName ?? '',
                'Agents': agents.join('; '),
                'Units': units,
                'Amount to Claim from Astern (RM)': Math.round(amount * 100) / 100,
                'Cust Progress %': r.custProgressPercent ?? '',
                'Customer Paid': r.customerPaid ?? '',
                'Customer Total': r.customerTotal ?? '',
            }));
        const cfg = mkConfig({ fieldSeparator: ',', useKeysAsHeaders: true, filename: 'Incentive_Ready_To_Claim_Astern' });
        download(cfg)(generateCsv(cfg)(rows.length ? rows : [{}]));
    };

    const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
        columns,
        data: visibleData,
        getRowId: (row) => String(row.claimId),
        // Plain substring search, not the default fuzzy match. Fuzzy matches a SUBSEQUENCE,
        // so searching "tan" returned every "Total Incentive" row -- T-o-t-a-l I-n -- and
        // buried the actual TAN LEE KHOON row under them. On a money table that reads as a
        // wrong answer, not a loose one. (user 2026-08-28)
        globalFilterFn: 'contains',
        // Only recordable, not-yet-received Total-Incentive rows are selectable (bulk receipt).
        enableRowSelection: (row) => {
            const r = row.original;
            return r.rowType === 'Total Incentive' && r.claimId > 0 && !r.asternReceived;
        },
        enableSelectAll: true,
        onRowSelectionChange: setRowSelection,
        positionToolbarAlertBanner: 'none',
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true,
        // Mobile: let vertical scroll flow to the page (no inner table scroll box).
        // Wide tables still scroll horizontally; sticky header just won't trap scroll.
        muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showGlobalFilter: true },
        state: {
            isLoading: loading,
            globalFilter: ts.globalFilter,
            sorting: ts.sorting,
            pagination: ts.pagination,
            columnFilters: ts.columnFilters,
            rowSelection,
        },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: ({ row }) => {
            const r = row.original;
            const hasChildren = Array.isArray(r.children) && r.children.length > 0;
            // A Total Incentive row with a real (positive) claimId is recordable — covers
            // battery (per-sale) AND case (per-agent lump) Totals.
            const recordable = r.rowType === 'Total Incentive' && r.claimId > 0;
            const clickable = hasChildren || recordable || (r.salesId > 0 && r.rowType === 'Incentive');
            return {
                onClick: () => {
                    if (hasChildren) { setCaseChildren(r); return; }
                    if (recordable) setAsternRow(r);
                    else if (r.salesId > 0 && r.rowType === 'Incentive') setPayoutRow(r);
                },
                sx: { cursor: clickable ? 'pointer' : 'default', '&:hover': { backgroundColor: '#F8FAFC' } },
            };
        },
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        renderTopToolbarCustomActions: ({ table }) => {
            const sel = table.getSelectedRowModel().rows.map(r => r.original);
            if (sel.length === 0) return null;
            const total = sel.reduce((s, r) => s + (r.asternAmount || 0), 0);
            return (
                <Button
                    variant="contained" size="small"
                    onClick={() => { setBulkDate(''); setBulkRemark(''); setBulkOpen(true); }}
                    sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontWeight: 600, borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)' } }}
                >
                    Mark Received from Astern ({sel.length}) · {handlePriceWithRM(total)}
                </Button>
            );
        },
        renderBottomToolbarCustomActions: ({ table }) => {
            const rows = table.getFilteredRowModel().rows.map(r => r.original);
            const fromAstern = rows.filter(r => r.rowType === 'Total Incentive').reduce((s, r) => s + (r.asternAmount || 0), 0);
            const toAgents = rows.filter(r => r.rowType === 'Incentive' && r.rewardType === 'Cash').reduce((s, r) => s + (r.amount || 0), 0);
            const webbey = rows.filter(r => r.rowType === 'Webbey Incentive').reduce((s, r) => s + (r.amount || 0), 0);
            return (
                <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#475569', fontFamily: "'Poppins', sans-serif", pl: '8px', flexWrap: 'wrap' }}>
                    <span>From Astern: {handlePriceWithRM(fromAstern)}</span>
                    <span>{'·'}</span>
                    <span>To Agents: {handlePriceWithRM(toAgents)}</span>
                    <span>{'·'}</span>
                    <span>Webbey: {handlePriceWithRM(webbey)}</span>
                </Typography>
            );
        },
    });

    const selectedClaimIds = Object.keys(rowSelection).filter(k => rowSelection[k]).map(Number);
    const selectedTotal = data
        .filter(r => r.rowType === 'Total Incentive' && selectedClaimIds.includes(r.claimId))
        .reduce((s, r) => s + (r.asternAmount || 0), 0);

    const doBulkReceipt = async () => {
        if (!bulkDate || selectedClaimIds.length === 0) return;
        setBulkBusy(true);
        try {
            const r = await recordAsternReceiptBulk({ claimIds: selectedClaimIds, receivedOn: bulkDate, remark: bulkRemark || null });
            const d = r.data || {};
            setBulkOpen(false);
            setRowSelection({});
            await fetchData();
            window.alert(`Marked ${d.processed ?? 0} claim(s) Received from Astern` + ((d.skipped ?? 0) > 0 ? ` · ${d.skipped} skipped (already received).` : '.'));
        } catch (err) {
            window.alert(err?.response?.data || err?.message || 'Bulk update failed.');
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label="Campaign"
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    sx={{ minWidth: 200, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem' } }}
                >
                    <MenuItem value="">All Campaigns</MenuItem>
                    {campaigns.map(c => {
                        const id = c.campaignId ?? c.id;
                        return (
                            <MenuItem key={id} value={id}>{c.name ?? c.campaignName ?? `#${id}`}</MenuItem>
                        );
                    })}
                </TextField>
                {/* Agent filter. Opening on All Campaigns put every campaign's claims in one
                    list — right, but it made a single agent hard to find by eye across ~2,000
                    rows. The question people actually bring to this page is "does THIS agent
                    have an incentive", so filter by the person, not just the campaign.
                    (user 2026-08-28) */}
                <TextField
                    select
                    size="small"
                    label="Agent"
                    value={agentFilter}
                    onChange={(e) => setAgentFilter(e.target.value)}
                    sx={{ minWidth: 200, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem' } }}
                >
                    <MenuItem value="">All Agents</MenuItem>
                    {agentOptions.map(a => (
                        <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{ minWidth: 150, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem' } }}
                >
                    <MenuItem value="">All Statuses</MenuItem>
                    {STATUS_OPTIONS.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                </TextField>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCsv}
                    sx={{ ml: 'auto', fontFamily: "'Poppins', sans-serif", textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                    Export CSV
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportReadyToClaim}
                    title="One line per sale whose customer has paid in full and whose battery money is not yet received from Astern"
                    sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                    Export Ready to Claim
                </Button>
                {isSuperAdmin && (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={regenerating}
                        onClick={handleRegenerateAll}
                        sx={{ ml: 1, fontFamily: "'Poppins', sans-serif", textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        {regenerating ? 'Regenerating…' : 'Regenerate All Claims'}
                    </Button>
                )}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', mb: 1.5, fontFamily: "'Poppins', sans-serif" }}>
                Workflow, same as a commission claim: a claim turns <strong>Ready to Claim</strong> once the customer has paid in full — claim that money from Astern. Click its Total Incentive row to record the money received, which makes the agent's rows Eligible. Then pay the agent from their Incentive row, or generate slips on the Slips page (Incentive tab) and approve them there. Nothing reaches a slip before the Astern money is recorded.
            </Typography>

            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <MaterialReactTable table={table} />
            </div>
            <IncentiveAsternModal open={!!asternRow} row={asternRow} onClose={() => setAsternRow(null)} onSaved={fetchData} />
            <IncentivePayoutModal open={!!payoutRow} row={payoutRow} onClose={() => setPayoutRow(null)} onSaved={fetchData} />

            {/* Bulk "Mark Received from Astern" — one date across all selected Total-Incentive rows. */}
            <Dialog open={bulkOpen} onClose={() => !bulkBusy && setBulkOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem' }}>
                    Mark Received from Astern
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontFamily: "'Poppins', sans-serif", mb: 2 }}>
                        {selectedClaimIds.length} claim(s) · {handlePriceWithRM(selectedTotal)} total. Each keeps its own amount; only the received date is applied to all.
                    </Typography>
                    <TextField
                        type="date" label="Received on" size="small" fullWidth
                        value={bulkDate} onChange={e => setBulkDate(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Remark (optional)" size="small" fullWidth
                        value={bulkRemark} onChange={e => setBulkRemark(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBulkOpen(false)} disabled={bulkBusy} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}>Cancel</Button>
                    <Button onClick={doBulkReceipt} disabled={bulkBusy || !bulkDate || selectedClaimIds.length === 0} variant="contained"
                        sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                        {bulkBusy ? 'Applying…' : `Mark ${selectedClaimIds.length} Received`}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!caseChildren} onClose={() => setCaseChildren(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem' }}>
                    {caseChildren?.agentName} — {caseChildren?.contribution} cases → {handlePriceWithRM(caseChildren?.amount ?? 0)}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mb: 1, fontFamily: "'Poppins', sans-serif" }}>
                        {caseChildren?.campaignName} · {caseChildren?.rewardLabel} · contributing sales:
                    </Typography>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Poppins', sans-serif" }}>
                        <thead>
                            <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase' }}>Sales #</th>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase' }}>Customer</th>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase', textAlign: 'right' }}>Cases</th>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase', textAlign: 'right' }}>Comm %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(caseChildren?.children ?? []).map((c, i) => (
                                <tr key={i} style={{ borderTop: '1px solid #F1F5F9' }}>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151' }}>{c.salesId}</td>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151' }}>{c.customerName || '—'}</td>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151', textAlign: 'right' }}>{Number(c.contribution ?? 0).toFixed(1)}</td>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151', textAlign: 'right' }}>{c.commProgressPercent != null ? `${c.commProgressPercent}%` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCaseChildren(null)} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#64748B' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
